// Who the hub's websocket session key belongs to, and when a new one is owed.
//
// The key (`gameliftStreamsSessionKey`) is minted when an INIT is built and every dashboard-websocket handler
// drops messages that do not carry it. UE, however, keeps the session it was FIRST INIT-ed with for the life of
// its process: a second INIT over the data channel does not move it (the known UE-side gap, measured
// 2026-08-12). So a key minted for a UE that is already logged in is a key nothing will ever send.
//
// Measured on the operator's own visit, 2026-09-17 14:05Z: the media watchdog rebuilt the peer twice on the SAME
// seat, each rebuild re-INIT-ed the same UE and minted a fresh key — and from then on the live UE's messages
// (~12 per second: position, regions, presence) were dropped for the rest of the visit, 15 minutes of
// "Skip handle WS message". The position feed rides that same filter, so the remembered spawn point froze too.
//
// The rule: a key is per UE PROCESS, not per peer connection. Mint one when the seat changes (a different lease
// = a different UE) or when the UE on this seat is replaced (D-28 `ue-reset` / slot restart); otherwise re-use
// the key the running UE already answers on. A page reload is the third case: the UE keeps its session but the
// page lost the key, so the key rides the stored lease (MytaStreamsService.saveStoredLease) and is adopted on
// rebind instead of being minted.

export interface ISessionKeyInput {
  /** The lease the INIT is about to go to, or null when there is no seat. */
  leaseId: string | null;
  /** The lease the current key was minted (or adopted) for; null when this page has no key yet. */
  keyedLeaseId: string | null;
  /** The UE on this seat has been replaced since the key was minted — a new process, hence a new session. */
  ueReplacedSinceKey: boolean;
}

export const shouldMintSessionKey = ({
  leaseId,
  keyedLeaseId,
  ueReplacedSinceKey,
}: ISessionKeyInput): boolean =>
  !keyedLeaseId || !leaseId || leaseId !== keyedLeaseId || ueReplacedSinceKey;

export const describeSessionKeyChoice = (input: ISessionKeyInput): string =>
  shouldMintSessionKey(input)
    ? !input.keyedLeaseId
      ? 'new session key (this page has none yet)'
      : input.leaseId !== input.keyedLeaseId
      ? 'new session key (a different seat, so a different UE)'
      : 'new session key (the UE on this seat was replaced)'
    : 'the session key this UE already answers on (no re-key)';
